import { createSelector } from 'reselect'

import * as SelectorUtils from './utils'
import { PLACEHOLDER_IMG, LIFESTYLE_PLACEHOLDER } from '../constants/commons'
import { DefaultTopicPlanAnticipationState } from '../reducers/topicPlans_reducer'
import { DefaultTopicPlanAnticipationTopLevelState } from '../reducers/topics_reducer'
import * as f from '../classes/Filters'

import TopicHelpers from '../classes/TopicHelpers'
import * as TLM from '../constants/tlm'

// Selectors used to reformat the state data into something terse we can plug more directly into the
// UI components.

// -----------------------------------------------------------------------------

// Supporting functions

// -----------------------------------------------------------------------------

function renderSocialMeta (data, smtype, meta){
  if (undefined !== data && data.length) {
    for (var i=0;i<data.length;i++){
      if (data[i].platform_type === smtype){
        if (undefined !== data[i].metrics && data[i].metrics.length){
          for (var j=0;j<data[i].metrics.length;j++){
            if (data[i].metrics[j].metric_type === meta){
              return (data[i].metrics[j].value)
            }
          }
        }
      }
    }
  }
  return '--'
}

function thumbnail (r) {
  if (r.entity_override_image) {
    return r.entity_override_image
  }
  if (r.thumbnail) {
    return r.thumbnail
  } else if (r.entity_type === 'Lifestyle') {
    return LIFESTYLE_PLACEHOLDER
  }
  return PLACEHOLDER_IMG
}

function getEntityRank (r, rankedEntities) {
  if (r.is_entity) {
    let entity = rankedEntities.find(e => e.entity_name === r.name)
    return entity ? entity.rank : null
  }
  return null
}

/** Reformat the plan details for use in the table */
function reformatRecommendations (recommendations, rankedEntities) {
  const recos = recommendations.map((recommendation, index) => {
    const r = recommendation.recommendation
    return {
      row_id: index+1,
      topic_id: r.id,
      topic_type: TopicHelpers.topicTypeFromDetails(r),
      topic_subtype: TopicHelpers.topicSubTypeFromDetails(r),
      topic_name: r.is_video ? r.title : r.name,
      thumbnail: thumbnail(r),
      // TBD about creating a link back to Diesel for entities?
      link: r.is_entity ? null : r.is_video ? `https://www.youtube.com/watch?v=${r.platform_assigned_id}` : `https://www.youtube.com/channel/${r.platform_assigned_id}`,
      parent_link: r.is_video ? `https://www.youtube.com/channel/${r.channel_platform_assigned_id}` : null,
      channel_platform_assigned_id: r.channel_platform_assigned_id,
      channel_name: r.channel_name,
      platform_assigned_id: r.platform_assigned_id,
      score: recommendation.score,
      segment: recommendation.segment,
      daily_views: renderSocialMeta(r.platform_profiles, 'Youtube', 'DailyViews'),
      total_subscribers: renderSocialMeta(r.platform_profiles, 'Youtube', 'TotalSubscriptions'),
      insta_followers: renderSocialMeta(r.platform_profiles, 'Instagram', 'TotalFollowers'),
      facebook_likes: renderSocialMeta(r.platform_profiles, 'Facebook', 'TotalLikes'),
      twitter_followers: renderSocialMeta(r.platform_profiles, 'Twitter', 'TotalFollowers'),
      labels: r.labels ? ',' + r.labels.join(',') + ',' : null,
      published_date: r.published_at,
      active_date: r.active_date,
      networkLabel: TopicHelpers.topicSubTypeFromDetails(r) === TLM.SHOW ? TopicHelpers.getNetworkLabel(r.labels) : null,
      rank: getEntityRank(r, rankedEntities),
      derivedLabels: TopicHelpers.topicSubTypeFromDetails(r) === TLM.BRAND ? TopicHelpers.getBrandDerivedLabels(r.labels) : null,
      category: TopicHelpers.topicSubTypeFromDetails(r) === TLM.SHOW ? TopicHelpers.getNetworkLabel(r.labels) : TopicHelpers.topicSubTypeFromDetails(r) === TLM.BRAND ? TopicHelpers.getBrandDerivedLabels(r.labels) : null
    }
  })

  return recos
}

// -----------------------------------------------------------------------------

// Topic Plan Selectors

// -----------------------------------------------------------------------------

/* Selector helper that extracts the topic id for the current page
 *
 * This is either a target of a shortcut, or something directly in the URL
 */
const getTopicId = (state, props) => {
  let topicId = null
  if (props.shortcut && props.shortcut.target_id) {
    topicId = props.shortcut.target_id
  } else {
    topicId = props.match.params.topicId
  }

  return topicId
}

/* Fetches the topics byId state */
const getTopicsById = (state) => {
  return state.topics.byId
}

const makeTopicPlanAnticipationRootSelector = () => {
  return createSelector(
    [getTopicId, getTopicsById],
    (topicId, topicsById) => {
      return (topicsById[topicId] || {}).anticipationPlan
    }
  )
}

const makeRankedEntitiesSelector = () => {
  return createSelector(
    [getTopicId, getTopicsById],
    (topicId, topicsById) => {
      return (topicsById[topicId] || {}).metrics
    }
  )
}

const makeTopicPlanAnticipation = () => {
  const getTopicPlan = makeTopicPlanAnticipationRootSelector()
  const getRanks = makeRankedEntitiesSelector()
  return createSelector(
    [getTopicPlan, getRanks],
    (topicPlan, ranks) => {
      let currentFilters = topicPlan ? topicPlan.currentFilters : f.Filters.Common.Anticipation
      let rankedEntities = ranks && ranks.details ? ranks.details.ranked_entities : []
      return topicPlanSelector(topicPlan, currentFilters, generateRankedEntitiesForShow(rankedEntities))
    }
  )
}

const generateRankedEntitiesForShow = (rankedEntities) => {
  let entities = []
  entities = rankedEntities.filter(e => e.entity_type === TLM.SHOW || e.entity_type === TLM.MOVIE)
  return entities
}

const topicPlanSelector = (topicPlan, currentFilters, rankedEntities) => {
  let plan = { ...DefaultTopicPlanAnticipationTopLevelState }
  let planFilters = f.Filters.Common.Anticipation

  if (topicPlan) {
    let currentFiltersPlan = topicPlan['anticipationPlan']
    if (currentFiltersPlan) {
      plan = currentFiltersPlan
      planFilters = currentFilters
    } else {
      let defaultFiltersPlan = topicPlan[f.Filters.Common.Anticipation]
      if (defaultFiltersPlan) {
        plan = defaultFiltersPlan
        planFilters = f.Filters.Common.Anticipation
      }
    }
  }
  // Default states take care of initializing these.
  const currentPage = plan.currentPage
  const endOfResult = plan.endOfResult
  let isFetching = false
  let error = null
  let details = null
  const recos = { recommendations: [] }
  const pages = Array(currentPage).fill().map((_, index) => index + 1)
  pages.forEach(page => {
    const pagePlan = plan[page] || { ...DefaultTopicPlanAnticipationState }
    const pageRecos = (pagePlan.details || {}).recommendations || []
    isFetching = isFetching || SelectorUtils.isFetching(pagePlan)
    error = error || pagePlan.error
    const uniqueIds = new Set()
    const mergedRecos = recos.recommendations.concat(pageRecos)
    const uniqueRecos = mergedRecos.filter(reco => {
      const isNew = !uniqueIds.has(reco.recommendation.id)
      uniqueIds.add(reco.recommendation.id)
      return isNew
    })
    recos.recommendations = uniqueRecos
  })
  details = {
    recommendations: reformatRecommendations(recos.recommendations, rankedEntities)
  }
  return {
    currentPage,
    endOfResult,
    isFetching,
    planFilters,
    error,
    details: details
  }
}

// -----------------------------------------------------------------------------

// Exports

// -----------------------------------------------------------------------------

export {
  makeTopicPlanAnticipation
}
